import { useEffect, useState } from 'react'
import { Field, Modal } from '@/shared/ui/Modal'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { explain } from '@/shared/api/problem'
import type { SurveyGroupDto } from '@/shared/api/types'
import { createGroup, updateGroup } from './api'

/**
 * Declaring a tracker, or renaming one.
 *
 * ## The code is the durable half
 *
 * `code` is how a poll says which tracker it belongs to, and the server enforces
 * uniqueness on it with a 409 whose sentence names the collision. It stays
 * EDITABLE on an existing tracker rather than being frozen: the server owns that
 * rule, and a client that greys the field out is guessing at a constraint it
 * cannot see. Offer the control, let the server refuse.
 *
 * There is no delete. `SurveyGroupController` has no `DELETE` — a tracker holding
 * waves that hold polls is the explanation of how those polls relate, and the
 * server does not offer to throw it away.
 */
export function TrackerDialog({ open, group, onClose, onSaved }: {
  open: boolean
  /** `null` is "declare a new tracker". An existing row is a rename. */
  group: SurveyGroupDto | null
  onClose: () => void
  onSaved: (saved: SurveyGroupDto) => void
}) {
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Re-seeded whenever the dialog is pointed at a different tracker, because the
  // component is not remounted between "edit NRB-GOV" and "edit WATER".
  useEffect(() => {
    if (!open) return
    setCode(group?.code ?? '')
    setName(group?.name ?? '')
    setDescription(group?.description ?? '')
    setError(null)
  }, [open, group])

  async function submit() {
    setBusy(true)
    setError(null)
    try {
      const body = {
        code: code.trim().toUpperCase(),
        name: name.trim(),
        description: description.trim() || undefined,
      }
      const saved = group ? await updateGroup(group.id, body) : await createGroup(body)
      onSaved(saved)
    } catch (e) {
      // `survey/group-code-taken` is the one that matters, and the server's
      // sentence explains what the code is FOR. Rephrasing it here would lose that.
      setError(explain(e))
    } finally {
      setBusy(false)
    }
  }

  const ready = code.trim().length > 0 && name.trim().length > 0

  return (
    <Modal
      open={open}
      onClose={onClose}
      width="md"
      title={group ? `Edit ${group.code}` : 'New tracker'}
      description="A tracker is a series of waves asking the same questions of the same population over time."
      footer={(
        <div className="flex items-center justify-end gap-2">
          <button type="button" className="erp-btn-ghost h-9 text-erp-sm" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="erp-btn-primary h-9 text-erp-sm"
            disabled={!ready || busy} onClick={submit}>
            {busy ? 'Saving…' : group ? 'Save' : 'Create'}
          </button>
        </div>
      )}
    >
      <div className="space-y-3">
        <ErrorStrip message={error} />

        <Field label="Code" required
          hint="Short and stable — this is how a poll says which tracker it belongs to. Uppercased on the way out.">
          <input className="erp-input h-9 w-full font-mono" value={code} maxLength={32}
            onChange={(e) => setCode(e.target.value)} placeholder="NRB-GOV" />
        </Field>

        <Field label="Name" required>
          <input className="erp-input h-9 w-full" value={name} maxLength={200}
            onChange={(e) => setName(e.target.value)} placeholder="Nairobi governor tracker" />
        </Field>

        <Field label="Description"
          hint="Who is being tracked, on what, and how often it is meant to be fielded.">
          <textarea className="erp-input w-full min-h-[72px] py-2" value={description}
            maxLength={4000} onChange={(e) => setDescription(e.target.value)} />
        </Field>

        <Note tone="info">
          Creating a tracker declares the series. It does not schedule anything and it does not
          field anything — waves are added one at a time, and a poll joins one by carrying its
          wave's id.
        </Note>
      </div>
    </Modal>
  )
}
